import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Warning as WarningIcon,
  AccountBalance as BillingIcon,
  Description as DocumentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { OnboardingProgressResponse, OnboardingStepUpdate } from '../../types/memberProfile';
import { memberProfileAPI, documentReviewAPI, paymentMethodAPI, memberGroupAPI } from '../../services/api';
import { DocumentReviewFlow } from './DocumentReviewFlow';
import { MemberGroup } from '../../types';
import { useAuth } from '../../context/AuthContext';

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  memberProfileId: string;
  onboardingData?: OnboardingProgressResponse;
  onRefreshOnboardingData: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  open,
  onClose,
  memberProfileId,
  onboardingData,
  onRefreshOnboardingData,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localOnboardingData, setLocalOnboardingData] = useState<OnboardingProgressResponse | null>(null);
  const [showDocumentReview, setShowDocumentReview] = useState(false);
  const [pendingDocumentsCount, setPendingDocumentsCount] = useState<number>(0);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [memberGroup, setMemberGroup] = useState<MemberGroup | null>(null);
  const [loadingMemberGroup, setLoadingMemberGroup] = useState(true);

  useEffect(() => {
    if (onboardingData) {
      setLocalOnboardingData(onboardingData);
    }
  }, [onboardingData]);

  useEffect(() => {
    if (open && memberProfileId) {
      loadPendingDocumentsCount();
      loadPaymentMethods();
      loadMemberGroup();
    }
  }, [open, memberProfileId]);

  const loadPendingDocumentsCount = async () => {
    try {
      setLoadingDocuments(true);
      const response = await documentReviewAPI.getPendingDocuments(memberProfileId);
      setPendingDocumentsCount(response.data.length);
    } catch (error: any) {
      console.error('Error loading pending documents:', error);
      // If there's an error, assume 0 documents
      setPendingDocumentsCount(0);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const response = await paymentMethodAPI.getMyPaymentMethods();
      const hasCards = response.data.paymentMethods && response.data.paymentMethods.length > 0;
      setHasPaymentMethod(hasCards);
      
      // Note: This is not entirely necessary, but a bit of a fallback 
      // If payment method exists but billing setup is not marked complete, update it
      if (hasCards && localOnboardingData && !localOnboardingData.onboardingStatus.billingSetupCompleted) {
        await updateBillingSetupStatus(true);
      }
    } catch (error: any) {
      console.error('Error loading payment methods:', error);
      setHasPaymentMethod(false);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const loadMemberGroup = async () => {
    try {
      setLoadingMemberGroup(true);
      // Get the member profile to check for memberGroupId
      const profileResponse = await memberProfileAPI.getMemberProfileById(memberProfileId);
      const profile = profileResponse.data;
      
      if (profile.memberGroupId) {
        const groupResponse = await memberGroupAPI.getById(profile.memberGroupId);
        setMemberGroup(groupResponse.data);
      } else {
        setMemberGroup(null);
      }
    } catch (error: any) {
      console.error('Error fetching member group:', error);
      setMemberGroup(null);
    } finally {
      setLoadingMemberGroup(false);
    }
  };

  const updateBillingSetupStatus = async (completed: boolean) => {
    try {
      await memberProfileAPI.updateOnboardingStep(memberProfileId, {
        step: 'billingSetup',
        completed,
      });
      await onRefreshOnboardingData();
    } catch (error) {
      console.error('Error updating billing setup status:', error);
    }
  };

  const getStepIcon = (step: string, completed: boolean) => {
    const IconComponent = step === 'billingSetup' ? BillingIcon : DocumentIcon;
    return completed ? (
      <CheckCircleIcon sx={{ color: 'success.main' }} />
    ) : (
      <IconComponent sx={{ color: 'warning.main' }} />
    );
  };

  const getStepInfo = (step: string) => {
    switch (step) {
      case 'billingSetup':
        const isInGroupBilling = memberGroup && 
          memberGroup.chargeToGroupPrimaryContact === true;
        
        let billingDescription = '';
        let isBillingCompleted = localOnboardingData?.onboardingStatus?.billingSetupCompleted || false;
        
        if (isInGroupBilling) {
          // Member is in a group where billing is handled by primary contact
          billingDescription = `Your billing is managed by the group "${memberGroup.groupName}". All charges are billed to the group's primary contact. No payment method setup is required for your account.`;
          isBillingCompleted = true; // Auto-complete for group billing members
        } else if (hasPaymentMethod) {
          billingDescription = 'Payment method on file. You can manage your billing information on the Billing page.';
        } else {
          billingDescription = 'Add a payment method to enable automatic payments and class bookings.';
        }
        
        return {
          title: 'Set Up Billing Information',
          description: billingDescription,
          completed: isBillingCompleted,
          actionable: !isInGroupBilling, // Not actionable if billing is managed by group
        };
      case 'documentReview':
        const isDocReviewCompleted = localOnboardingData?.onboardingStatus?.documentReviewCompleted || false;
        const hasPendingDocs = pendingDocumentsCount > 0;
        
        return {
          title: 'Review Club Documents',
          description: hasPendingDocs
            ? `Click here to read and acknowledge ${pendingDocumentsCount} club document${pendingDocumentsCount > 1 ? 's' : ''}.`
            : isDocReviewCompleted
              ? 'All club documents have been reviewed and acknowledged.'
              : loadingDocuments
                ? 'Loading documents...'
                : 'No documents to review at this time.',
          completed: isDocReviewCompleted,
          actionable: hasPendingDocs,
        };
      default:
        return {
          title: 'Unknown Step',
          description: 'Please contact support for assistance.',
          completed: false,
          actionable: false,
        };
    }
  };

  const handleStepClick = (step: string) => {
    const stepInfo = getStepInfo(step);
    
    // Don't allow clicking on non-actionable steps
    if (!stepInfo.actionable) {
      return;
    }
    
    if (step === 'documentReview' && pendingDocumentsCount > 0) {
      setShowDocumentReview(true);
    } else if (step === 'billingSetup') {
      // Close modal and navigate to billing page
      onClose();
      navigate('/billing');
    }
  };

  const handleDocumentReviewComplete = async () => {
    setShowDocumentReview(false);
    // Refresh onboarding data and payment methods
    await onRefreshOnboardingData();
    await loadPendingDocumentsCount();
    await loadPaymentMethods();
  };

  const handleDocumentReviewBack = () => {
    setShowDocumentReview(false);
  };

  // Steps are read-only - users cannot manually mark them as complete

  // Manual completion removed - steps must be completed through actual processes

  // Dismissing instructions is not allowed - modal will show until onboarding is complete

  if (!localOnboardingData || loadingMemberGroup) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  // Both steps are mandatory, but document review is only shown if enabled
  const allSteps = ['billingSetup'];
  if (localOnboardingData?.documentReviewEnabled) {
    allSteps.push('documentReview');
  }
  
  const completedSteps = allSteps.filter(step => {
    const stepInfo = getStepInfo(step);
    return stepInfo.completed;
  });

  const progress = (completedSteps.length / allSteps.length) * 100;

  if (showDocumentReview) {
    return (
      <Dialog 
        open={open} 
        onClose={() => {}} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DocumentReviewFlow
          memberProfileId={memberProfileId}
          onComplete={handleDocumentReviewComplete}
          onBack={handleDocumentReviewBack}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            Welcome! Let's Get You Started
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${completedSteps.length}/${allSteps.length} Complete`}
              color={localOnboardingData.hasCompletedMandatory ? 'success' : 'warning'}
              variant="outlined"
            />
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Complete the following steps to setup your profile. <br />
          If you have any questions or need assistance, feel free to contact our staff.
          
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Progress: {Math.round(progress)}%
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 8,
              bgcolor: 'grey.200',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${progress}%`,
                height: '100%',
                bgcolor: localOnboardingData.hasCompletedMandatory ? 'success.main' : 'warning.main',
                transition: 'width 0.3s ease-in-out',
              }}
            />
          </Box>
        </Box>

        <List>
          {allSteps.map((step, index) => {
            const stepInfo = getStepInfo(step);
            return (
              <React.Fragment key={step}>
                <ListItem
                  sx={{
                    border: 1,
                    borderColor: stepInfo.completed ? 'success.main' : 'grey.300',
                    borderRadius: 1,
                    mb: 2,
                    bgcolor: stepInfo.completed ? 'success.50' : 'background.paper',
                    cursor: stepInfo.actionable && !stepInfo.completed ? 'pointer' : 'default',
                    '&:hover': stepInfo.actionable && !stepInfo.completed ? {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main',
                    } : {},
                  }}
                  onClick={() => stepInfo.actionable && !stepInfo.completed && handleStepClick(step)}
                >
                  <ListItemIcon>
                    {getStepIcon(step, stepInfo.completed)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {stepInfo.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {stepInfo.description}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    {stepInfo.completed ? (
                      <CheckCircleIcon sx={{ color: 'success.main' }} />
                    ) : (
                      <UncheckedIcon sx={{ color: 'grey.400' }} />
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>

        {localOnboardingData.hasCompletedMandatory && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              🎉 Congratulations! You've completed all mandatory onboarding steps. 
              You now have full access to all club features.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingModal;